import React, { useEffect } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { FeatherCheck, FeatherAlertTriangle } from "@subframe/core";
import { ValidationResponse } from "../../domain/entities/ValidationEntity";
import { BillEntity } from "../../domain/entities/BillEntity";
import { useBillValidation } from "../hooks/useBillValidation";
import { AI_DETECTION_FINAL_ERROR_MESSAGE } from "../../../../shared/constants/aiDetectionMessages";

const VALIDATION_LABELS: Record<string, string> = {
  itc_eligibility: "Input Tax Credit (B2B/B2C)",
  vendor_gst_match: "Vendor GST Match",
  buyer_gst_match: "Buyer GST Match",
  destination_of_supply: "Destination Of Supply",
  source_of_supply: "Source Of Supply",
  inter_intra_gst: "Inter/Intra GST",
  line_total: "Line Total",
  document_number_check: "Document Number",
  duplicate_check: "Duplicate Check",
  gl_classification: "GL Classification",
  tds_classification: "TDS Classification",
  rcm_recommendation: "Reverse Charge (RCM)",
  itc_section17: "ITC Check",
  hsn_check: "HSN Check",
  hsn_description_check: "HSN Description Check",
  hsn_rate_check: "HSN Rate Check",
  // ZOUR-65: which GST registration the document belongs to. Named for what
  // the reviewer must DO, not for the internal key.
  location_missing: "Location Not Identified",
  location_unconfirmed: "Location — Please Confirm",
  location_inactive: "Location No Longer Active",
  buyer_gstin_not_ours: "Buyer GSTIN Not Yours",
  // ZOUR-19: raised when a document predates the GST registration date and
  // carried tax that was dropped.
  gst_not_applicable_before_date: "GST Not Applicable (Before Registration)",
};

const formatValidationName = (name: string): string =>
  VALIDATION_LABELS[name] ??
  name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

interface AIDetectionOverviewBillsProps {
  billData: any; // BillData type
  billDocument: BillEntity;
  onValidationComplete?: (result: ValidationResponse) => void;
  onLoadingChange?: (loading: boolean) => void;
}

const AIDetectionOverviewBills: React.FC<AIDetectionOverviewBillsProps> = ({
  billData,
  billDocument,
  onValidationComplete,
  onLoadingChange,
}) => {
  // Use the custom hook for validation
  const { validationData, loading, error } = useBillValidation({
    billData,
    billDocument,
    onValidationComplete,
  });

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => {
      onLoadingChange?.(false);
    };
  }, [loading, onLoadingChange]);

  if (loading) {
    return (
      <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-warning-200 px-6 py-6 bg-gradient-to-r from-warning-50 to-warning-100">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-warning-500">
            <span className="text-body-bold font-body-bold text-white">AI</span>
          </div>
          <span className="text-heading-3 font-heading-3 text-default-font">
            Running AI Detection...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-error-200 px-6 py-6 bg-gradient-to-r from-error-50 to-error-100">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-error-500">
            <span className="text-body-bold font-body-bold text-white">AI</span>
          </div>
          <span className="text-heading-3 font-heading-3 text-default-font">
            {AI_DETECTION_FINAL_ERROR_MESSAGE}
          </span>
        </div>
      </div>
    );
  }

  if (!validationData) {
    return null;
  }

  const { validation_summary } = validationData;
  const passedCount = validation_summary.passed_validations;
  const totalCount = validation_summary.total_validations;
  const failedCount = validation_summary.failed_validations;

  // Filter validation details into passed and failed arrays
  const passedValidations = validation_summary.validation_details.filter(
    (validation) => validation.valid,
  );
  const failedValidations = validation_summary.validation_details.filter(
    (validation) => !validation.valid,
  );

  return (
    <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-warning-200 px-6 py-6 bg-gradient-to-r from-warning-50 to-warning-100">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-warning-500">
          <span className="text-body-bold font-body-bold text-white">AI</span>
        </div>
        <div className="flex items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            AI Detection Overview
          </span>
          <Badge variant="warning" icon={<FeatherCheck />}>
            {passedCount}/{totalCount} Checks Passed
          </Badge>
        </div>
      </div>
      <div className="flex w-full items-start gap-6">
        <div
          className={`flex ${
            failedCount === 0 ? "w-full" : "grow shrink-0 basis-0"
          } flex-col items-start gap-4 rounded-md border border-solid border-success-200 bg-success-50 px-4 py-4`}
        >
          <div className="flex items-center gap-2">
            <IconWithBackground variant="success" size="small" />
            <span className="text-heading-3 font-heading-3 text-success-700">
              Passed Checks ({passedCount})
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-2">
            {passedValidations.map((validation, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-success-500" />
                <span className="text-body font-body text-success-700">
                  {formatValidationName(validation.validation_name)}
                  : {validation.message}
                </span>
              </div>
            ))}
          </div>
        </div>
        {failedCount > 0 && (
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-4">
            <div className="flex items-center gap-2">
              <IconWithBackground
                variant="error"
                size="small"
                icon={<FeatherAlertTriangle />}
              />
              <span className="text-heading-3 font-heading-3 text-error-700">
                Issues Found ({failedCount})
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              {failedValidations.map((validation, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-error-500" />
                  <span className="text-body font-body text-error-700">
                    {formatValidationName(validation.validation_name)}
                    : {validation.message}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIDetectionOverviewBills;
